import { useGT } from 'gt-react';

import type { UnloadedSpaWorkbookLabels } from './writeUnloadedSpaWorkbook';

/** The report workbook's copy, translated here because its writer has no translator. */
export function useUnloadedSpaLabels(): UnloadedSpaWorkbookLabels {
  const gt = useGT();
  const sheet = 'worksheet name in the ABB unloaded SPA report';
  const column = 'column heading in the ABB unloaded SPA report';
  const summary =
    'row label on the Summary sheet of the ABB unloaded SPA report';
  return {
    sheets: {
      customersNotLoaded: gt('1. Not Loaded - Your Customers', {
        context: `${sheet}: the listed customers' SPAs that are not loaded`,
      }),
      customersLoaded: gt('2. Already Loaded (FYI)', {
        context: `${sheet}: the listed customers' SPAs already loaded`,
      }),
      customersWithoutSpa: gt('3. No ABB SPA Found', {
        context: `${sheet}: listed customers no ABB SPA names`,
      }),
      divisionNotLoaded: gt('4. All Division Not Loaded', {
        context: `${sheet}: every SPA of the Division not loaded anywhere`,
      }),
      loadedGroups: gt('5. Group SPAs - Loaded Accts', {
        context: `${sheet}: loaded group SPAs beside the accounts they are loaded on`,
      }),
      otherDivision: gt('6. Loaded in Other Banner', {
        context: `${sheet}: SPAs loaded only under another Division`,
      }),
      summary: gt('Summary', { context: sheet }),
    },
    columns: {
      customerName: gt('Customer Name (Sold to)', { context: column }),
      customerNumber: gt('Customer Number (Sold to)', { context: column }),
      status: gt('Status', { context: column }),
      meaning: gt('What this means', { context: column }),
      alsoLoaded: gt('Customer also has other SPAs already loaded?', {
        context: column,
      }),
      quote: gt('ABB SPA (Quote)', { context: column }),
      endCustomerName: gt('ABB End Customer Name', { context: column }),
      endCustomerNumber: gt('ABB End Customer #', { context: column }),
      nameMatch: gt('Name Match', { context: column }),
      spaType: gt('SPA Type', { context: column }),
      endCustomerCount: gt('# End Customers on SPA', { context: column }),
      branches: gt('Branch (col C)', { context: column }),
      quoteDate: gt('Quote Date', { context: column }),
      validFrom: gt('Valid From', { context: column }),
      validTo: gt('Valid To', { context: column }),
      revision: gt('Revision', { context: column }),
      loadedAccounts: gt('Division accounts loaded on this SPA', {
        context: column,
      }),
      loadedDivisions: gt('Divisions SPA is loaded in', { context: column }),
      yourCustomer: gt('Your customer', { context: column }),
      note: gt('Note', { context: column }),
      item: gt('Item', { context: column }),
      value: gt('Value', { context: column }),
    },
    status: {
      'not loaded': gt('SPA is not loaded in the Division at all', {
        context: 'status of an SPA in the ABB unloaded SPA report',
      }),
      'missing for account': gt(
        "SPA is loaded in the Division, but NOT on this customer's account",
        { context: 'status of an SPA in the ABB unloaded SPA report' },
      ),
      loaded: gt('Loaded to this account', {
        context: 'status of an SPA in the ABB unloaded SPA report',
      }),
      'other division': gt('Loaded under another banner', {
        context: 'status of an SPA in the ABB unloaded SPA report',
      }),
    },
    match: {
      exact: gt('Exact', {
        context: 'how sure a customer name matches an ABB end customer name',
      }),
      strong: gt('Strong', {
        context: 'how sure a customer name matches an ABB end customer name',
      }),
      possible: gt('Possible - check', {
        context:
          'a customer name only possibly matches an ABB end customer name; a person should check',
      }),
    },
    singleCustomer: gt('Single customer', {
      context: 'SPA type: the SPA names one end customer',
    }),
    group: gt('Group', {
      context: 'SPA type: the SPA names several end customers',
    }),
    yes: gt("Yes - see 'Already Loaded' sheet", {
      context: 'the customer has other SPAs already loaded',
    }),
    no: gt('No', { context: 'the customer has no other SPA already loaded' }),
    allBranches: (count) =>
      gt('All {count} Division branches', {
        count,
        context: 'the SPA names every branch of the Division',
      }),
    meaning: ({ single, status, count }) => {
      if (single) {
        return gt(
          'Single-customer SPA for this customer only - needs loading',
          {
            context: 'explains a missing SPA in the ABB unloaded SPA report',
          },
        );
      }
      return status === 'missing for account'
        ? gt(
            'Group SPA ({count} customers) - other customers on it are loaded, this one was missed',
            {
              count,
              context: 'explains a missing SPA in the ABB unloaded SPA report',
            },
          )
        : gt(
            'Group SPA ({count} customers) - not loaded for anyone in the Division',
            {
              count,
              context: 'explains a missing SPA in the ABB unloaded SPA report',
            },
          );
    },
    noSpa: gt("No ABB SPA found for this customer at the Division's branches", {
      context: 'note for a listed customer that no ABB SPA names',
    }),
    elsewhere: (name, quotes, divisions) =>
      gt(
        "No ABB SPA found at the Division's branches (similar name '{name}' is on SPA {quotes} at Division {divisions} branches only)",
        {
          name,
          quotes,
          divisions,
          context:
            "note for a listed customer whose name only appears on another Division's SPAs",
        },
      ),
    otherDivisionNote: gt(
      'Loaded under another banner - cannot be shared to this Division, so not a gap',
      { context: 'note on an SPA loaded only under another Division' },
    ),
    summary: {
      spaList: gt('ABB SPA list', { context: summary }),
      results: gt('Results Workbook', { context: summary }),
      division: gt('Division', { context: summary }),
      branches: gt("Branches read as the Division's", { context: summary }),
      spas: gt("SPAs at the Division's branches", { context: summary }),
      loaded: gt('...loaded in the Division', { context: summary }),
      notLoaded: gt('...not loaded in any Division', { context: summary }),
      otherDivision: gt('...loaded under another banner only (not a gap)', {
        context: summary,
      }),
      customers: gt('Customers listed', { context: summary }),
      customersMissing: gt('...with an SPA missing from their account', {
        context: summary,
      }),
      customersWithoutSpa: gt('...with no ABB SPA found', { context: summary }),
      caveat: gt('Caveat', { context: summary }),
      caveatText: gt(
        "ABB numbers its end customers with its own customer numbers, so customers are matched to them by name (see 'Name Match'). A person should check 'Possible' matches.",
        { context: summary },
      ),
    },
  };
}
